<!-- SettingsModal.vue -->

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from "vue";
import { useSettingStore } from "@/stores/useSettingStore";
import { useColorUtils } from "@/composables/useColorUtils";
import { useLinkedHighlightColors } from "@/composables/useLinkedHighlightColors";
import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close"]);
const settingsStore = useSettingStore();

const { colorTextToBackground } = useColorUtils();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const activeView = ref("default-style");

// OBJ REFATORADO: defaultRequiredBackground e defaultUnderlineColor foram removidos.
const obj = ref({
  defaultColor: "#000000",
  defaultBackground: "#FFFFFF",
  defaultUnderlineColor: "#000000",
});

// Régua de "temperatura" (0–100): modula a cor derivada da última escolhida -
// o fundo a partir do texto, ou o texto a partir do fundo.
const DEFAULT_TEMPERATURE = 75;
const temperature = ref(DEFAULT_TEMPERATURE);

// Segura a régua durante o carregamento inicial: a temperatura salva é
// atribuída no load, e recalcular ali sobrescreveria as cores salvas.
const isLoading = ref(true);

const isSaving = ref(false);
const toastTimeoutId = ref(null);

// ─── Texto e fundo ligados nos dois sentidos ────────────────────────────────
// Escolher o texto recalcula o fundo (pastel) e escolher o fundo recalcula o
// texto; a régua de temperatura recalcula a cor que não foi a última escolhida.
// O sublinhado acompanha sempre a cor do texto.
const { pickTextColor, pickBackgroundColor } = useLinkedHighlightColors({
  textColor: computed({
    get: () => obj.value.defaultColor,
    set: (color) => {
      obj.value.defaultColor = color;
      obj.value.defaultUnderlineColor = color;
    },
  }),
  backgroundColor: computed({
    get: () => obj.value.defaultBackground,
    set: (color) => {
      obj.value.defaultBackground = color;
    },
  }),
  temperature,
  isPaused: () => isLoading.value,
});

// ✅ TOAST (padrão do ProfileForm)
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  return new Promise((resolve) => {
    if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = setTimeout(() => {
      showToast.value = false;
      toastTimeoutId.value = null;
      resolve();
    }, duration);
  });
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(5000);
}

async function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  await resetToastTimeout(2400);
}

function closeModal() {
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = null;
  }
  emit("close");
}

// Mapeia os dados do store para o objeto local 'obj'
function mapStoreToLocal() {
  const setting = settingsStore.setting;

  if (setting) {
    // Temperatura salva (fonte da verdade do fundo). Definida antes do cálculo
    // de fallback abaixo, que a utiliza.
    temperature.value = setting.uncategorizedTemperature ?? DEFAULT_TEMPERATURE;

    obj.value.defaultColor = setting.uncategorizedTextColor || "#000000";
    obj.value.defaultBackground =
      setting.uncategorizedBackgroundColor ||
      colorTextToBackground(obj.value.defaultColor, temperature.value);
    obj.value.defaultUnderlineColor =
      setting.uncategorizedUnderlineColor || obj.value.defaultColor;
  } else {
    temperature.value = DEFAULT_TEMPERATURE;
    obj.value.defaultColor = "#000000";
    obj.value.defaultBackground = colorTextToBackground("#000000", temperature.value);
    obj.value.defaultUnderlineColor = "#000000";
  }
}

// LÓGICA DE CARREGAMENTO REAL
async function fetchCurrentStyleSettings() {
  await settingsStore.fetchSetting();
  mapStoreToLocal();
}

// Mapeamento do obj.value para o DTO que será enviado para a API
const mapLocalToDto = () => {
  return {
    uncategorizedTextColor: obj.value.defaultColor,
    uncategorizedBackgroundColor: obj.value.defaultBackground,
    uncategorizedUnderlineColor: obj.value.defaultUnderlineColor,
    uncategorizedTemperature: temperature.value,
  };
};

const singleWordPreviewStyle = computed(() => ({
  backgroundColor: "transparent",
  color: obj.value.defaultColor,
  borderRadius: "999px",
  padding: "0.15rem 0.15rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: "1.4",
  verticalAlign: "baseline",
  fontWeight: "600",
  border: "none",
}));

// Estilo para Trecho com Várias Palavras (apenas background)
const multiWordPreviewStyle = computed(() => ({
  display: "inline-block",
  padding: "1px 3px",
  borderRadius: "0.5rem",
  fontWeight: "400",
  color: "var(--text-dark)",
  backgroundColor: obj.value.defaultBackground,
  lineHeight: "1.05",
}));

// ✅ LÓGICA DE SALVAMENTO REAL com TOAST
async function saveChanges() {
  if (activeView.value !== "default-style") return;

  isSaving.value = true;

  try {
    const dto = mapLocalToDto();
    await settingsStore.saveSetting(dto);

    await showSuccessToast("Estilo de nota padrão salvo com sucesso!");
    closeModal();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Falha ao salvar. Tente novamente.";

    console.error("Falha ao salvar as alterações de estilo:", error);
    showErrorToast(errorMessage);
  } finally {
    isSaving.value = false;
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  await fetchCurrentStyleSettings();
  // Libera a régua só após o flush do carregamento, preservando as cores
  // salvas. A partir daqui, mexer na régua recalcula a cor derivada.
  await nextTick();
  isLoading.value = false;
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Preferências</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar modal"
        >
          <svg
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <main class="content-area">
          <div
            v-if="activeView === 'default-style'"
            class="style-settings-view"
          >
            <header class="view-header">
              <div class="view-title-row">
                <span class="palette-badge">
                  <i class="fas fa-palette"></i>
                </span>
                <h3 class="view-title">Estilo dos destaques dos textos</h3>
              </div>
              <p class="view-description">
                Defina as cores padrão para os trechos destacados: cor do texto
                para palavras únicas e cor de fundo para trechos com várias palavras.
              </p>
              <div class="info-badge">
                <i class="fas fa-info-circle info-badge-icon"></i>
                <span>
                  Essa configuração será usada para marcação visual padrão nos
                  seus textos, quando tags não forem atribuídas à nota.
                </span>
              </div>
            </header>

            <div class="form-content">
              <section class="shared-preview-section">
                <h4 class="section-title">Pré-visualização</h4>
                <div class="shared-preview-shell">
                  <blockquote class="poem-blockquote">
                    <p class="poem-line">No silêncio da tarde, a luz repousa,</p>
                    <p class="poem-line">sobre a janela antiga e sonhadora;</p>
                    <p class="poem-line">
                      e o vento leva,
                      <span :style="singleWordPreviewStyle">manso</span>, a memória,
                    </p>
                    <p class="poem-line">
                      de um
                      <span :style="multiWordPreviewStyle">tempo feito de sombra</span>
                      e rosa.
                    </p>
                  </blockquote>
                </div>
              </section>

              <section class="style-section">
                <p class="style-intro">
                  Defina como o destaque será exibido ao selecionar:
                </p>

                <div class="style-subsection">
                  <h4 class="subsection-title">Palavras únicas</h4>
                  <div class="pickers-only-section">
                    <div class="color-pickers">
                      <div class="color-picker-group first-color-picker-group inline-picker-group">
                        <label for="color-default" class="form-label">Cor do texto</label>

                        <!-- ✅ PICKER REFATORADO:
                             o input nativo fica invisível e o círculo visível
                             é controlado por este wrapper, eliminando as arestas
                             quadradas internas do type="color". -->
                        <span
                          class="color-picker-shell"
                          :style="{ backgroundColor: obj.defaultColor }"
                        >
                          <input
                            type="color"
                            id="color-default"
                            :value="obj.defaultColor"
                            @input="pickTextColor($event.target.value)"
                            class="color-input"
                            aria-label="Selecionar cor do texto"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <hr class="pickers-separator" />

              <section class="style-section style-section-continued">
                <div class="style-subsection">
                  <h4 class="subsection-title">Trechos com várias palavras</h4>
                  <div class="pickers-only-section">
                    <div class="multi-word-row">
                      <div class="color-picker-group multi-word-bg-group">
                        <label for="color-background-multi" class="form-label">Cor do fundo</label>

                        <!-- Escolher o fundo recalcula a cor do texto. -->
                        <span
                          class="color-picker-shell"
                          :style="{ backgroundColor: obj.defaultBackground }"
                        >
                          <input
                            type="color"
                            id="color-background-multi"
                            :value="obj.defaultBackground"
                            @input="pickBackgroundColor($event.target.value)"
                            class="color-input"
                            aria-label="Selecionar cor do fundo"
                          />
                        </span>
                      </div>

                      <div class="temperature-group">
                        <div class="temperature-label-row">
                          <span class="temperature-value">{{ temperature }}</span>
                        </div>
                        <input
                          type="range"
                          id="temperature-range"
                          min="0"
                          max="100"
                          step="1"
                          v-model.number="temperature"
                          class="temperature-range"
                        />
                        <div class="temperature-scale-labels">
                          <span>Frio</span>
                          <span>Quente</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>

      <div class="modal-footer">
        <button type="button" @click="closeModal" class="btn btn-secondary">
          Fechar
        </button>
        <button
          type="button"
          @click="saveChanges"
          class="btn btn-success"
          :disabled="isSaving"
        >
          <span v-if="isSaving" class="spinner"></span>
          {{ isSaving ? "Salvando..." : "Salvar Alterações" }}
        </button>
      </div>
    </div>
  </div>

  <!-- ✅ TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[30000] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 36rem;
  height: 82vh;
  min-height: 650px;
  max-height: 650px;
}

.content-area {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.spinner {
  width: 14px;
  margin-right: 0.45rem;
}

.style-settings-view {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  height: 100%;
}

.view-header {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
}

.view-title-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;
}

.palette-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1.5px solid #93c5fd;
  background-color: var(--info-bg);
  color: var(--info);
  font-size: 0.8rem;
  flex-shrink: 0;
}

.view-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-dark);
}

.view-description {
  text-align: justify;
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--text-light);
  margin: 0;
  line-height: 1.4;
}

.info-badge {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.45rem;
  background-color: var(--brand-tint);
  border: 1px solid #c7d2fe;
  color: #4338ca;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.4;
}

.info-badge-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
  font-size: 0.85rem;
}

:global(.dark) .info-badge {
  background-color: #1e1b4b;
  border-color: #3730a3;
  color: #a5b4fc;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.shared-preview-section,
.style-section {
  padding-top: 0.5rem;
}

.shared-preview-section {
  padding-top: 0.1rem;
}

.style-section-continued {
  padding-top: 0.15rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.25rem 0;
}

.style-intro {
  margin: 0 0 0.9rem 0;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: var(--text-light);
}

.style-subsection {
  padding-left: 0.1rem;
}

.subsection-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.35rem 0;
  line-height: 1.25;
}

.pickers-separator {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 0.1rem 0 0;
}

.shared-preview-shell {
  width: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 0.45rem 1.1rem 0.35rem;
  box-sizing: border-box;
  background: var(--bg-content);
}

.poem-blockquote {
  margin: 0;
  width: 100%;
  padding-left: 0.55rem;
  border-left: 4px solid #b39aff;
  font-style: italic;
  color: var(--text-dark);
}

.poem-line {
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.pickers-only-section {
  display: block;
}

.color-pickers {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.first-color-picker-group {
  margin: 0;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.toggle-control-group {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: auto;
  flex: 0 0 auto;
  cursor: pointer;
}

.toggle-label-text {
  white-space: nowrap;
  font-weight: 500;
}

.toggle-switch {
  position: relative;
  display: block;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: var(--neutral-400);
  transition: background-color 0.2s ease;
  flex: 0 0 auto;
}

.toggle-switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.toggle-input:checked + .toggle-switch {
  background-color: var(--success);
}

.toggle-input:checked + .toggle-switch::after {
  transform: translateX(16px);
}

.checkbox-and-picker {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.color-picker-subgroup {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  transition: opacity 0.3s;
}

.color-picker-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: opacity 0.3s;
}

.color-picker-group.invisible,
.color-picker-subgroup.invisible {
  opacity: 0;
  pointer-events: none;
}

/* =========================================================
   ✅ PICKER CIRCULAR REFATORADO
   =========================================================
   O input[type="color"] nativo costuma renderizar internamente um swatch
   quadrado. Mesmo com border-radius, alguns navegadores deixam aparecer
   pequenas arestas internas. Por isso, o input fica invisível e ocupa todo
   o círculo; o elemento visível é .color-picker-shell.
   ========================================================= */

.color-picker-shell {
  position: relative;
  display: inline-flex;
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  min-height: 1.5rem;
  border-radius: 999px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  isolation: isolate;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.18);
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.color-picker-shell::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  z-index: 2;
}

.color-picker-shell:hover {
  transform: scale(1.05);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.1),
    0 2px 6px rgba(0, 0, 0, 0.22);
}

.color-picker-shell:focus-within {
  outline: none;
  box-shadow:
    0 0 0 2px var(--brand),
    0 1px 3px rgba(0, 0, 0, 0.18);
}

.color-input {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.color-input::-webkit-color-swatch-wrapper {
  padding: 0;
  border: 0;
}

.color-input::-webkit-color-swatch {
  border: 0;
}

.color-input::-moz-color-swatch {
  border: 0;
}

.multi-word-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.multi-word-bg-group {
  flex: 0 0 auto;
  justify-content: flex-start;
  gap: 0.6rem;
}

.inline-picker-group {
  justify-content: flex-start;
  gap: 0.6rem;
}

.temperature-group {
  flex: 0 0 52%;
  min-width: 0;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.temperature-label-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.temperature-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-dark);
  font-variant-numeric: tabular-nums;
  min-width: 2ch;
  text-align: right;
}

.temperature-range {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    #93c5fd 0%,
    var(--neutral-200) 50%,
    #fca35a 100%
  );
  cursor: pointer;
  outline: none;
}

.temperature-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--brand);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.temperature-range::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.temperature-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--brand);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.temperature-range:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 3px;
}

.temperature-scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

@media (max-width: 520px) {
  .modal-backdrop {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .modal-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .multi-word-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }

  .temperature-group {
    width: 100%;
    flex-basis: auto;
  }

  .modal-footer {
    justify-content: space-between;
  }
}

</style>